import { CoverIsFor } from '../types/types';
import {
  age65Rebates,
  age69Rebates,
  age70Rebates,
  REBATE_VERSION,
} from './config';
import nib from '../assets/images/covers/nib.png';
import hcf from '../assets/images/covers/hcf.png';
import bup from '../assets/images/covers/bup.png';
import ahm from '../assets/images/covers/ahm.png';
import hbf from '../assets/images/covers/hbf.png';
import mbp from '../assets/images/covers/mbp.png';
import cbh from '../assets/images/covers/cbhs.png';
import hif from '../assets/images/covers/hif.png';
import lhs from '../assets/images/covers/lhs.png';
import fai from '../assets/images/covers/fai.png';
import gmh from '../assets/images/covers/gmh.png';
import gmf from '../assets/images/covers/gmf.png';
import ama from '../assets/images/covers/ama.png';
import ahb from '../assets/images/covers/ahb.png';
import cps from '../assets/images/covers/CUA.png';
import cwh from '../assets/images/covers/westfund.png';
import cdh from '../assets/images/covers/cdh.jpg';
import auf from '../assets/images/covers/auf.png';
import aca from '../assets/images/covers/aca.jpg';
import hci from '../assets/images/covers/hci.png';
import hea from '../assets/images/covers/hea.png';
import mdh from '../assets/images/covers/mdh.jpg';
import omf from '../assets/images/covers/omf.avif';
import nhb from '../assets/images/covers/nhb.png';
import sps from '../assets/images/covers/sps.png';
import lhm from '../assets/images/covers/lhm.png';
import pwa from '../assets/images/covers/pwa.webp';
import spe from '../assets/images/covers/spe.png';
import qch from '../assets/images/covers/qch.png';
import rte from '../assets/images/covers/RTE.jpg';
import rbh from '../assets/images/covers/rbh.png';
import slm from '../assets/images/covers/slm.png';
import ntf from '../assets/images/covers/ntf.png';
import tfs from '../assets/images/covers/tfs.png';
import qtu from '../assets/images/covers/qtu.png';
import wfd from '../assets/images/covers/wfd.png';
import fra from '../assets/images/covers/fra.png';
import hhbf from '../assets/images/covers/hhbf.png';
import nwm from '../assets/images/covers/nmw.png';
import myo from '../assets/images/covers/myo.webp';
import bda from '../assets/images/covers/bda.png';
import cbc from '../assets/images/covers/cbc.png';
import esh from '../assets/images/covers/esh.png';
import {
  extrasCoverFeatures,
  hospitalCoverFeatures,
  STATE_MAP,
} from './constants';
import { isAHM } from './form/helpers';

export const isEmpty = (...values: any) => {
  const isEmptyValue = (value: any) => {
    // Handle null or undefined
    if (value === null || value === undefined) {
      return true;
    }

    // Handle Date (invalid Date is considered empty)
    if (value instanceof Date) {
      return isNaN(value.getTime());
    }

    // Handle numbers (NaN is considered empty, but 0 is not)
    if (typeof value === 'number') {
      return isNaN(value);
    }

    // Handle booleans (false is not empty)
    if (typeof value === 'boolean') {
      return false;
    }

    // Handle functions (functions are not empty)
    if (typeof value === 'function') {
      return false;
    }

    // Handle symbols (symbols are not empty)
    if (typeof value === 'symbol') {
      return false;
    }

    // Handle objects (empty objects are considered empty)
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    // Handle strings (empty or whitespace-only strings are empty)
    if (typeof value === 'string') {
      if (value === 'undefined') return true;
      return value.trim().length === 0;
    }

    // Fallback for other types (e.g., BigInt)
    return false;
  };

  // Return true if every value is considered empty
  return values.every(isEmptyValue);
};
export const isEqual = (obj1: any, obj2: any): boolean => {
  // If both are the same reference, return true
  if (obj1 === obj2) return true;

  // If either is null or not an object, return false
  if (
    obj1 === null ||
    typeof obj1 !== 'object' ||
    obj2 === null ||
    typeof obj2 !== 'object'
  ) {
    return false;
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // If the number of keys is different, return false
  if (keys1.length !== keys2.length) return false;

  // Recursively compare each key's value
  for (const key of keys1) {
    if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const getLastFirstOfJuly = (date = new Date()) => {
  const currentYear = date.getFullYear();
  const firstOfJuly = new Date(currentYear, 6, 1); // July is month 6 (0-indexed)

  if (date >= firstOfJuly) {
    return firstOfJuly;
  } else {
    return new Date(currentYear - 1, 6, 1);
  }
};

export const calculateAgeBasedOnCAE = (dateOfBirth: string) => {
 const [day, month, year] = dateOfBirth.split('/').map(Number);
  const dob = new Date(year, month - 1, day); // month is 0-indexed

  const today = new Date();
  
  // Decide the reference year:
  const referenceYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  const referenceDate = new Date(referenceYear, 6, 1); // July 1st of referenceYear

  let age = referenceDate.getFullYear() - dob.getFullYear();

  // Check if birthday has not occurred yet by July 1st of referenceYear
  const birthdayThatYear = new Date(referenceYear, dob.getMonth(), dob.getDate());
  if (birthdayThatYear > referenceDate) {
    age--;
  }

  return age;
};

export const calculateAge = (dateOfBirth: string) => {
  if (!dateOfBirth) {
    return 0;
  }

  const [day, month, year] = dateOfBirth.split('/').map(Number);

  if (!day || !month || !year) {
    return 0;
  }

  const dob = new Date(year, month - 1, day); // JavaScript months are 0-indexed

  if (isNaN(dob.getTime())) {
    return 0;
  }
  //use latest 1th of july as the age calculation date, if this year passed the 1th of july, use this year as the age calculation date
  const today = new Date();
  const july1LastYear = new Date(today.getFullYear() - 1, 6, 1); // 6 is July (0-indexed)
  let age = july1LastYear.getFullYear() - dob.getFullYear();

  // Adjust age if birthday hasn't occurred yet this year
  const hasBirthdayPassed =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

  if (!hasBirthdayPassed) {
    age--;
  }

  return age;
};

export const getRebateVersion = () => {
  return REBATE_VERSION.new;
};

export const getRebate = (dateOfBirth: string[], tierIndex: number) => {
  if (tierIndex === 4) {
    return '0';
  }
  const oldest = dateOfBirth
    .filter((dob) => dob !== '-')
    .map((dataString) => {
      const [day, month, year] = dataString.split('/').map(Number);
      return new Date(year, month - 1, day);
    })
    .sort((a, b) => a.getTime() - b.getTime())[0];

  const age = calculateAge(oldest?.toLocaleDateString('en-GB'));
  const rebateVersion = getRebateVersion();

  if (age < 65) {
    return rebateVersion.age65[tierIndex];
  } else if (age < 70) {
    return rebateVersion.age69[tierIndex];
  } else {
    return rebateVersion.age70[tierIndex];
  }
};

export const getRebatePercentageFromAge = (
  age: number,
  tierIndex: number,
): number => {
  let rebatePercentage = '0%';
  const rebateVersion = getRebateVersion();

  if (age < 65) {
    rebatePercentage = rebateVersion.age65[tierIndex];
  } else if (age < 70) {
    rebatePercentage = rebateVersion.age69[tierIndex];
  } else {
    rebatePercentage = rebateVersion.age70[tierIndex];
  }

  return Number.parseFloat(rebatePercentage.slice(0, -1)) / 100;
};

export const formatDate = (dateString: any) => {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };

  // Create a day suffix
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';

  // Format the date
  return `${day}${suffix} ${date?.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })}`;
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    useGrouping: true,
  }).format(value);
};

export const refactorCustomerType = (customerType: string) => {
  if (customerType === CoverIsFor.Single) return 'Single';
  if (customerType === CoverIsFor.Couple) return 'Partner';
  if (customerType === CoverIsFor.Family) return 'Family';
  if (customerType === CoverIsFor.SingleParentFamily) return 'Single_Parent';
  return 'Single';
};

export const formatTypeOfCover = (typeOfCover: string) => {
  if (typeOfCover === 'Extras') {
    return 'Extra';
  }
  if (typeOfCover === 'Hospital & Extras') {
    return 'Hospital + Extra';
  }
  return typeOfCover;
};

export const getHealthFundImagePath = (healthFundId: number) => {
  switch (healthFundId) {
    case 1:
      return aca;
    case 2:
      return ahm;
    case 3:
      return auf;
    case 4:
      return bup;
    case 5:
      return cbh;
    case 6:
      return cdh;
    case 7:
      return cwh;
    case 8:
      return cps;
    case 9:
      return ahb;
    case 10:
      return ama;
    case 11:
      return gmf;
    case 12:
      return gmh;
    case 13:
      return fai;
    case 14:
      return hbf;
    case 15:
      return hcf;
    case 16:
      return hci;
    case 17:
      return hif;
    case 18:
      return sps;
    case 19:
      return hea;
    case 20:
      return lhs;
    case 21:
      return mbp;
    case 22:
      return mdh;
    case 23:
      return omf;
    case 24:
      return nhb;
    case 25:
      return nib;
    case 26:
      return lhm;
    case 27:
      return pwa;
    case 28:
      return spe;
    case 29:
      return qch;
    case 30:
      return rte;
    case 31:
      return rbh;
    case 32:
      return slm;
    case 33:
      return ntf;
    case 34:
      return tfs;
    case 35:
      return qtu;
    case 36:
      return wfd;
    case 37:
      return fra;
    case 38:
      return hhbf;
    case 39:
      return nwm;
    case 40:
      return myo;
    case 41:
      return bda;
    case 42:
      return cbc;
    case 43:
      return esh;
    default:
      return '';
  }
};

export const getPaymentInfo = (healthFundId: number, paymentMethod: string) => {
  switch (healthFundId) {
    case 1:
      return (
        <ul>
          <li>
            The price displayed reflects a 4% direct debit discount for paying
            via bsb/account, please note that your premium will increase by 4%
            if you opt to pay via credit card.
          </li>
          <li>
            Please note, if there is a difference between your cover start date
            and your first payment date, you will pay a pro-rata payment on the
            first payment to make up for the difference between the two dates.
          </li>
        </ul>
      );
    case 2:
      return (
        <ul>
          <li>
            We can set up your payments fortnightly, monthly or yearly with ahm.
            If you wish to pay weekly, you can change your payment frequency
            using the ahm member portal once you become a member.
          </li>
          <li>
            Mastercard and Visa payments incur a 0.21% surcharge, and you can
            avoid this if you choose Direct Debit from your bank account.
          </li>
        </ul>
      );

    case 3:
      return paymentMethod === 'Credit Card' ? (
        <ul>
          <li>
            If you choose to pay via credit card, then Australian Unity will
            deduct the first premium within 0-24 hours of submission, regardless
            of your cover start date and will deduct your second payment one
            payment frequency after your cover start date.
          </li>
          <li>
            The price displayed includes a 4% direct debit discount for paying
            via credit card or bsb/account, if you choose to change your payment
            method to manual pay, you will lose this discount.
          </li>
        </ul>
      ) : (
        <ul>
          <li>
            If you choose to pay via bsb/account, then you cover start date must
            match your first payment date.
          </li>
          <li>
            The price displayed includes a 4% direct debit discount for paying
            via credit card or bsb/account, if you choose to change your payment
            method to manual pay, you will lose this discount.
          </li>
        </ul>
      );
    case 4:
      return (
        <ul>
          <li>
            Please note, that Bupa does not allow credit card as a payment
            option when opting to pay on a fortnightly basis.
          </li>
          <li>
            If paying fortnightly, please be aware that Bupa will deduct one
            month premium as your first payment, and then it will revert to
            fortnightly afterwards.
          </li>
          <li>
            We need to align your cover start date with your first payment date,
            regardless of the payment frequency.
          </li>
        </ul>
      );
    case 17:
      return (
        <ul>
          <li>
            Score an additional 2% discount by paying half yearly or 4% discount
            for a whole year.
          </li>
          <li>
            Please note HIF will not be able to deduct any premiums within the
            first 3 business days. If there is a gap between your cover start
            date and your first payment date, you will pay a small pro-rata
            payment on your first payment to cover the difference between the
            dates.
          </li>
        </ul>
      );
    case 25:
      return (
        <ul>
          <li>
            The price displayed reflects a 4% direct debit discount for paying
            via bsb/account, please note that your premium will increase by 4%
            if you opt to pay via credit card.
          </li>
          <li>
            Please note, if there is a difference between your cover start date
            and your first payment date, you will pay a pro-rata payment on the
            first payment to make up for the difference between the two dates.
          </li>
        </ul>
      );

    case 36:
      return (
        <ul>
          <li>
            Please note, if paying fortnightly, Westfund can only process
            payments on a Thursday.
          </li>
          <li>
            Please note, if paying monthly, Westfund can only process payments
            on the 1st or 17th of each month.
          </li>
          <li>
            If there is a difference between your cover start date and your
            first payment date, you will pay a pro-rata difference to cover the
            difference between the two dates.
          </li>
        </ul>
      );
    default:
      return '';
  }
};

export const getPaymentRules = (healthFundId: number) => {
  switch (healthFundId) {
    //AHM
    case 2:
      return (
        <>
          <h3>AHM Payment Details</h3>
          <h3>Flexible Payment Options</h3>
          <ul>
            <li>
              You can pay weekly, fortnightly, monthly, quarterly, half-yearly
              or yearly. Set up a direct debit to match your pay cycle—it’s your
              choice.
            </li>
          </ul>
          <h3>Payment Methods</h3>
          <h3>Pay by direct debit from your bank account or credit card.</h3>
          <ul>
            <li>A 0.21% surcharge applies to Visa and Mastercard.</li>
            <li>Avoid the surcharge by choosing bank account direct debit.</li>
          </ul>
        </>
      );
    case 3:
      return (
        <>
          <h3>Australian Unity Payment Details</h3>
          <h3>
            Payment Options: Choose from fortnightly, monthly, quarterly,
            6-monthly or yearly.
          </h3>
          <h3>Credit Card:</h3>
          <ul>
            <li>
              Your first payment will be deducted within 24 hours of joining,
              even if your cover starts later.
            </li>
            <li>
              Your ongoing payments will begin one payment cycle after your
              cover start date.
            </li>
          </ul>
          <h3>
            Bank Account (BSB): Your first payment will be taken on your cover
            start date.
          </h3>
        </>
      );
    //BUPA
    case 4:
      return (
        <>
          <h3>Bupa Payment Details</h3>
          <ul>
            <li>Your cover start date must match your first payment date.</li>
            <li>
              You can’t select the 29th, 30th or 31st of any month as your cover
              start date.
            </li>
            <li>
              Credit card payments are not available if you choose to pay
              fortnightly.
            </li>
          </ul>
        </>
      );
    //HIF
    case 17:
      return (
        <>
          <h3>Important Information About Your First Payment with HIF</h3>
          <ul>
            <li>
              No Weekend Payments: You won’t be able to select a Saturday or
              Sunday as your first payment date.
            </li>
            <li>
              Earliest Payment Date: You can only choose a payment date that’s
              at least three days from today. For example, if today is Sunday,
              the earliest available date will be Wednesday.
            </li>
          </ul>
          <h3>Save More with Upfront Payments:</h3>
          <ul>
            <li>Get 2% off when you pay 6 months upfront</li>
            <li>Get 4% off when you pay 12 months upfront</li>
          </ul>
        </>
      );
    //NIB
    case 25:
      return (
        <>
          <h3>NIB Payment Details</h3>
          <ul>
            <li>
              Fortnightly Payments: Payment dates can’t fall on Saturday or
              Sunday.
            </li>
            <li>
              Monthly, Quarterly, 6-Monthly & Yearly Payments: You can’t choose
              the 28th, 29th, 30th or 31st as your payment date.
            </li>
          </ul>
          <h3>Save with Bank Account Payments:</h3>
          <ul>
            <li>
              Prices shown include a 4% discount for paying via bank account
              (BSB).
            </li>
            <li>
              If you choose to pay via credit card, your premium will increase
              by 4%.
            </li>
          </ul>
        </>
      );
    //Westfund
    case 36:
      return (
        <>
          <h3>Westfund Payment Information</h3>
          <ul>
            <li>
              Fortnightly Payments: These are only available on Thursdays.
            </li>
            <li>
              Monthly Payments: You can choose either the 1st or the 17th of
              each month.
            </li>
            <li>
              Payment Method: Westfund supports direct debit only for both
              fortnightly and monthly payments.
            </li>
          </ul>
        </>
      );
    default:
      return '';
  }
};

export const calculateAgeAsAtJuly1 = (dateOfBirth: string) => {
  if (!dateOfBirth) {
    return 0;
  }

  const [day, month, year] = dateOfBirth.split('/').map(Number);

  if (!day || !month || !year) {
    return 0;
  }

  const dob = new Date(year, month - 1, day); // JavaScript months are 0-indexed

  if (isNaN(dob.getTime())) {
    return 0;
  }

  // Get current date
  const today = new Date();

  // Get 1 July of the previous year
  const july1LastYear = new Date(today.getFullYear() - 1, 6, 1); // 6 is July (0-indexed)

  let age = july1LastYear.getFullYear() - dob.getFullYear();

  // Adjust age if birthday hasn't occurred yet on July 1
  const hasBirthdayPassed =
    july1LastYear.getMonth() > dob.getMonth() ||
    (july1LastYear.getMonth() === dob.getMonth() &&
      july1LastYear.getDate() >= dob.getDate());

  if (!hasBirthdayPassed) {
    age--;
  }

  return age;
};

export const calculateLHC = (dateOfBirth: string) => {
  const age = calculateAgeAsAtJuly1(dateOfBirth);
  if (age < 31) {
    return 0;
  }
  const yearsOver30 = age - 30;
  const loadingPercentage = yearsOver30 * 2;

  //capped at 70%
  const lhcLoading = Math.min(loadingPercentage, 70);

  return lhcLoading < 0 ? 0 : lhcLoading;
};

export const getHospitalFeatureIcon = (featureName: string) => {
  const feature = hospitalCoverFeatures.find(
    (feature) => feature.name?.toLowerCase() === featureName?.toLowerCase(),
  );
  if (!feature) return hospitalCoverFeatures[0].icon;
  return feature.icon;
};
export const getExtrasFeatureIcon = (featureName: string) => {
  const feature = extrasCoverFeatures.find(
    (feature) => feature.name?.toLowerCase() === featureName?.toLowerCase(),
  );
  if (!feature) return extrasCoverFeatures[0].icon;
  return feature.icon;
};

export const showUniQuestionts = (healthFundId: number, age: number) => {
  
  if (healthFundId === 2 || healthFundId === 17 || healthFundId === 25) {
    if (age <= 30 && age >= 21) {
      return true;
    } else {
      return false;
    }
  } else if (healthFundId === 4) {
    if (age <= 31 && age >= 21) {
      return true;
    } else {
      return false;
    }
  } else if (healthFundId === 3) {
    if (age <= 30 && age >= 23) {
      return true;
    } else {
      return false;
    }
  } else if (healthFundId === 7) {
    return false;
  }

  return false;
};

export const getFirstPaymentDate = (
  healthFundId: number,
  paymentFrequency: string,
  coverStartDate: string,
  paymentMethod: string,
) => {
  switch (healthFundId) {
    //AHM
    case 2:

    //australian unity
    // eslint-disable-next-line no-fallthrough
    case 3:
      if (paymentMethod === 'Credit Card') {
        return `first payment date needs to be one payment frequency after
cover start date. Eg. Customer pays Monthly, and 17" August is Cover Start
Date, then 18° September needs to be ongoing payment date`;
      }
      if (paymentMethod === 'Bank Account') {
        return `The cover start date and payment date need
to be the same date`;
      }

    //BUPA
    case 4:

    //HIF
    case 17:

    //NIB
    case 25:

    //Westfund
    case 36:
  }
};

export const getPaymentDateError = (
  healthFundId: number,
  paymentFrequency: string,
  paymentDate: string,
  paymentMethod: string,
  coverStartDate: string,
) => {
  switch (healthFundId) {
    //australian unity

    case 3:
      if (paymentFrequency === 'Monthly') {
        if (
          paymentDate === '29' ||
          paymentDate === '30' ||
          paymentDate === '31'
        ) {
          return `Please select a valid payment date for the selected payment frequency`;
        }
      }
      if (paymentDate !== coverStartDate) {
        return `The cover start date and payment date need to be the same`;
      }
      return;

    //BUPA
    case 4:
      if (
        coverStartDate.split('-')[2] === '29' ||
        coverStartDate.split('-')[2] === '30' ||
        coverStartDate.split('-')[2] === '31'
      ) {
        return `Policy not allowed to start on the selected date`;
      }
      if (coverStartDate !== paymentDate) {
        return `Cover start date and payment date need to be the same`;
      }
      if (
        paymentFrequency === 'Fortnightly' &&
        paymentMethod === 'Credit Card'
      ) {
        return `Please note, Bupa does not allow credit card as a payment option when opting to pay on a fortnightly basis.`;
      }
      return;
    //HIF
    case 17:
      if (
        paymentFrequency === 'Monthly' ||
        paymentFrequency === 'Yearly' ||
        paymentFrequency === 'Quarterly' ||
        paymentFrequency === '6 Monthly'
      ) {
        if (
          paymentDate.split('-')[2] === '28' ||
          paymentDate.split('-')[2] === '29' ||
          paymentDate.split('-')[2] === '30' ||
          paymentDate.split('-')[2] === '31'
        ) {
          return `28, 29, 30, 31 are not allowed as payment dates for ${paymentFrequency} payments`;
        }
      }
      if (paymentFrequency === 'Fortnightly') {
        const [year, month, day] = paymentDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);

        const dayOfWeek = date.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          return `Satuday and Sunday not allowed as payment days on fortnightly payment frequency`;
        }
      }
      return;

    //NIB
    case 25:
      if (
        paymentFrequency === 'Monthly' ||
        paymentFrequency === 'Yearly' ||
        paymentFrequency === 'Quarterly' ||
        paymentFrequency === '6 Monthly'
      ) {
        if (
          paymentDate.split('-')[2] === '28' ||
          paymentDate.split('-')[2] === '29' ||
          paymentDate.split('-')[2] === '30' ||
          paymentDate.split('-')[2] === '31'
        ) {
          return `28, 29, 30, 31 are not allowed as payment dates for ${paymentFrequency} payments`;
        }
      }
      if (paymentFrequency === 'Fortnightly') {
        const [year, month, day] = paymentDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);

        const dayOfWeek = date.getDay();

        if (dayOfWeek === 0 || dayOfWeek === 6) {
          return `Satuday and Sunday not allowed as payment days on fortnightly payments`;
        }
      }
      return;
    //Westfund
    case 36:
      if (paymentFrequency === 'Fortnightly') {
        const [year, month, day] = paymentDate.split('-').map(Number);

        const date = new Date(year, month - 1, day);

        const dayOfWeek = date.getDay();

        if (dayOfWeek !== 4) {
          return `Only thursdays allowed as payment dates for fortnightly payments`;
        }
      }

      if (paymentFrequency === 'Monthly') {
        if (
          paymentDate.split('-')[2] !== '01' &&
          paymentDate.split('-')[2] !== '17'
        ) {
          return `Only 1st and 17th allowed as payment dates for ${paymentFrequency} payments`;
        }
      }

      if (
        paymentFrequency === 'Quarterly' ||
        paymentFrequency === '6 Monthly' ||
        paymentFrequency === 'Yearly'
      ) {
        return `Bsb/account and credit card are not available for ${paymentFrequency} payments`;
      }
      return;

    default:
      return;
  }
};

export const getAgeFromLoading = (loading: number) => {
  if (loading < 0 || loading > 70) {
    throw new Error('Loading percentage must be between 0 and 70.');
  }

  if (loading === 0) return 18; // Age when loading is 0%

  if (loading <= 60) {
    return 30 + loading / 2; // Linear increase from 31 to 60
  }

  return 70; // Ag
};

export const formatHtml = (htmlString: string) => {
  return htmlString
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/\ufffd/g, "'");
};

export type CoverType = 'Single' | 'Partner' | 'Family' | 'Single_Parent';

export const shouldPopulatePartner = (coverType: CoverType) =>
  coverType === 'Partner' || coverType === 'Family';

export const shouldPopulateDependant = (coverType: CoverType) =>
  coverType === 'Family' || coverType === 'Single_Parent';

export const cleanRequestBody = (
  formData: any,
  updatedSalesReqBody: any,
  customerType: CoverType,
  applyHealthFundId: number,
) => {
  const cleanedBody = { ...updatedSalesReqBody };

  //delete unused fields
  delete cleanedBody.payment.allowDirectDebit;
  delete cleanedBody.customer.fullName;
  if (customerType === 'Single') {
    delete cleanedBody.partner;
    delete cleanedBody.dependants;
  }

  //if the customer is a partner and has no health fund, remove previous fund code and number
  if (customerType === 'Partner' && formData.hasPartnerHealthFund === 'No') {
    delete cleanedBody.partner.previous_fund_code;
    delete cleanedBody.partner.previous_fund_number;
  }

  // ✅ FIX: Add null/undefined check before calling split
  if (formData.partnerHealthFund && formData.partnerHealthFund !== '0.0') {
    const partnerHealthFundParts = formData.partnerHealthFund.split('.');
    if (partnerHealthFundParts.length > 1) {
      const partnerHealHealthFundId = partnerHealthFundParts[1];
      if (partnerHealHealthFundId === '0') {
        delete cleanedBody.partner?.previous_fund_code;
        delete cleanedBody.partner?.previous_fund_number;
      }
    }
  }

  if (!shouldPopulateDependant(customerType)) {
    delete cleanedBody.dependants;
  } else {
    for (let i = 0; i < cleanedBody.dependants.length; i++) {
      const dependant = cleanedBody.dependants[i];

      if (dependant.education_details.full_time === false) {
        delete dependant.education_details.institution_code;
        delete dependant.education_details.institution_name;
        delete dependant.education_details.student_number;
        delete dependant.education_details.start_date;
        delete dependant.education_details.completion_date;
      }
    }
  }

  // only ahl need to provide student medicare details
  if (!isAHM(applyHealthFundId)) {
    if (cleanedBody?.dependants) {
      cleanedBody.dependants.forEach((_: any, idx: number) => {
        delete cleanedBody.dependants[idx].identification_details;
      });
    }
  }

  if (!shouldPopulatePartner(customerType)) {
    delete cleanedBody.partner;
  }

  // ✅ FIX: Add null/undefined check before calling split
  if (formData.healthFund) {
    const healthFundParts = formData.healthFund.split('.');
    if (healthFundParts.length > 1) {
      const healthFundId = healthFundParts[1];
      if (healthFundId === '0') {
        delete cleanedBody.customer.previous_fund_code;
        delete cleanedBody.customer.previous_fund_number;
      }
    }
  }

  if (!isEmpty(cleanedBody.customer.medicare_position)) {
    cleanedBody.customer.medicare_position = parseInt(
      cleanedBody.customer.medicare_position,
    );
  }

  if (shouldPopulatePartner(customerType) && !isEmpty(cleanedBody.partner)) {
    if (!isEmpty(cleanedBody.partner.medicare_position)) {
      cleanedBody.partner.medicare_position = parseInt(
        cleanedBody.partner.medicare_position,
      );
    }
  }

  if (shouldPopulateDependant(customerType)) {
    for (let i = 0; i < cleanedBody.dependants.length; i++) {
      if (!isEmpty(cleanedBody.dependants[i].medicare_position)) {
        cleanedBody.dependants[i].medicare_position = parseInt(
          cleanedBody.dependants[i].medicare_position,
        );
      }
    }
  }

  // format line 1 and line2
  const { line1: residentialLine1, line2: residentialLine2 } =
    formatAddressLines(
      cleanedBody.residential_address?.line_1,
      cleanedBody.residential_address?.line_2,
    );
  cleanedBody.residential_address.line_1 = residentialLine1;
  cleanedBody.residential_address.line_2 = residentialLine2;

  const { line1: postalLine1, line2: postalLine2 } = formatAddressLines(
    cleanedBody.postal_address?.line_1,
    cleanedBody.postal_address?.line_2,
  );
  cleanedBody.postal_address.line_1 = postalLine1;
  cleanedBody.postal_address.line_2 = postalLine2;

  if (cleanedBody.residential_address.line_2 === '') {
    delete cleanedBody.residential_address.line_2;
  }
  if (cleanedBody.postal_address?.line_2 === '') {
    delete cleanedBody.postal_address?.line_2;
  }
  if (cleanedBody.dependants) {
    cleanedBody.dependants.forEach((dependent: any) => {
      const dependentAge = dependent.age;

      if (!showUniQuestionts(applyHealthFundId, dependentAge)) {
        delete dependent.education_details;
      }
    });
  }

  return cleanedBody;
};
const FREQUENCY_LIST = [
  'Fortnightly',
  'Monthly',
  'Quarterly',
  '6 Monthly',
  'Yearly',
];

export const getFundPaymentFrequency = (
  healthFundId: number,
  paymentMethod?: string,
) => {
  switch (healthFundId) {
    // Westfund doesn't allow Quarterly, 6 Monthly, Yearly
    case 36:
      return FREQUENCY_LIST.filter(
        (frequency) =>
          frequency !== 'Quarterly' &&
          frequency !== '6 Monthly' &&
          frequency !== 'Yearly',
      );
    //Bupa doesn't allow credit card for fortnightly payments
    case 4:
      if (paymentMethod && paymentMethod === 'Credit Card') {
        return FREQUENCY_LIST.filter(
          (frequency) => frequency !== 'Fortnightly',
        );
      } else {
        return FREQUENCY_LIST;
      }

    default:
      return FREQUENCY_LIST;
  }
};

const formatAddressLines = (oldLine1: string, oldLine2: string) => {
  const MAX_LINE_1_LENGTH = 60;
  const MAX_LINE_2_LENGTH = 30;
  const addressLine1 = oldLine1.replace(/[^a-zA-Z0-9\s/]/g, '');
  const addressLine2 = oldLine2.replace(/[^a-zA-Z0-9\s/]/g, '');

  const line1Words = addressLine1.split(' ');
  let line1 = '';
  const remainingWords: string[] = [];

  line1Words.some((word: any) => {
    if ((line1 + word).length <= MAX_LINE_1_LENGTH) {
      line1 += (line1 ? ' ' : '') + word;
      return false;
    } else {
      remainingWords.push(word);
      return true;
    }
  });

  let line2 = remainingWords.join(' ');
  if (line2.length > 0) {
    line2 = (addressLine2 ? addressLine2 + ' ' : '') + line2;
    const line2Words = line2.split(' ');
    line2 = '';
    line2Words.some((word) => {
      if ((line2 + word).length <= MAX_LINE_2_LENGTH) {
        line2 += (line2 ? ' ' : '') + word;
        return false;
      } else {
        return true;
      }
    });
  }

  return { line1, line2 };
};

type AnyObject = {
  [key: string]: any;
};

export function trimStringsInObject<T>(obj: T): T {
  if (typeof obj === 'string') {
    // If it's a string, trim it
    return obj.trim() as T;
  } else if (Array.isArray(obj)) {
    // If it's an array, recursively apply the function to each element
    return obj.map((item) => trimStringsInObject(item)) as unknown as T;
  } else if (obj && typeof obj === 'object') {
    // If it's an object, recursively apply the function to each property
    const trimmedObj: AnyObject = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        trimmedObj[key] = trimStringsInObject(obj[key]);
      }
    }
    return trimmedObj as T;
  }

  // If it's not a string, array, or object, return it as is
  return obj;
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

export const pushDataLayer = (event: string, data: any) => {
  if (window && window.dataLayer) {
    window.dataLayer.push({
      event,
      ...data,
    });
  }
};

export type State = keyof typeof STATE_MAP;

export const getStateId = (state: State) => {
  const stateId = STATE_MAP[state] || 0;

  return stateId;
};

export function validateMedicareNumber(medicareNo: string): boolean {
  // Remove non-digit characters
  if (!medicareNo) return false;
  const cleanedMedicareNo = medicareNo.replace(/\D/g, '');

  // Check for 11 digits
  if (cleanedMedicareNo.length === 10) {
    // Test leading digit and checksum
    const match = cleanedMedicareNo.match(/^([2-6]\d{7})(\d)/);
    if (match) {
      const base = match[1];
      const checkDigit = parseInt(match[2], 10);
      let sum = 0;
      const weights = [1, 3, 7, 9, 1, 3, 7, 9];

      for (let i = 0; i < weights.length; i++) {
        sum += parseInt(base[i], 10) * weights[i];
      }

      return sum % 10 === checkDigit;
    }
  }
  return false;
}

export function splitAddress(
  address: string,
  maxLength: number = 40,
): [string, string] {
  const words = address.split(' ');
  let line1 = '';
  let line2 = '';

  for (const word of words) {
    if (line1.length + word.length <= maxLength) {
      line1 += (line1 ? ' ' : '') + word;
    } else if (line2.length + word.length <= maxLength) {
      line2 += (line2 ? ' ' : '') + word;
    } else {
      break; // Stop if words cannot fit within constraints
    }
  }
  if (line1.length > maxLength) {
    line1 = line1.slice(0, maxLength);
  }
  if (line2.length > maxLength) {
    line2 = line2.slice(0, maxLength);
  }
  //strip comma if it is the last character
  if (line1.endsWith(',')) {
    line1 = line1.slice(0, -1);
  }
  if (line2.endsWith(',')) {
    line2 = line2.slice(0, -1);
  }
  return [line1, line2];
}

export const calculateSaving = (
  oldPricePerMonth: number,
  newPrice: number,
  paymentFrequency: 'Monthly' | 'Yearly' | 'Fortnightly',
): string => {
  const newPricePerMonth =
    paymentFrequency === 'Monthly'
      ? newPrice
      : paymentFrequency === 'Yearly'
        ? newPrice / 12
        : newPrice * 26;

  const oldPricePerYear = oldPricePerMonth * 12;
  const newPricePerYear = newPricePerMonth * 12;
  const baseSavings = oldPricePerYear - newPricePerYear;

  if (baseSavings < 0) {
    return '0.00';
  }
  return baseSavings.toFixed(2);
};

export const refactorCoverIsFor = (coverIsFor: string) => {
  if (coverIsFor === CoverIsFor.SingleParentFamily) {
    return 'Single_Parent';
  } else if (coverIsFor === CoverIsFor.Couple) {
    return 'Partner';
  } else if (coverIsFor === CoverIsFor.Family) {
    return 'Family';
  } else if (coverIsFor === CoverIsFor.Single) {
    return 'Single';
  }
  return coverIsFor;
};

export const sanitizedUtmData = (object: any) => {
  //if data field is empty, set it to organic
  Object.keys(object).forEach((key) => {
    if (!object[key]) {
      object[key] = 'organic';
    }
  });
  return object;
};

export const getStepName = (step: number) => {
  switch (step) {
    case -1:
      return 'Bupa Flextras cover';
    case 0:
      return 'Your Details';
    case 1:
      return 'Partner Details';
    case 2:
      return 'Dependant Details';
    case 3:
      return 'Medicare Details';
    case 4:
      return 'Address Details';
    case 5:
      return 'Previous Fund';
    case 6:
      return 'Payment Details';
    case 7:
      return 'Confirm';
    case 8:
      return 'Submit';

    default:
  }
};

interface Person {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  [key: string]: any;
}

export const toFullName = (customer: Person) => {
  const { first_name, last_name, middle_name } = customer;
  if (!first_name) return '';
  return `${first_name} ${middle_name ? middle_name + ' ' : ''}${last_name}`;
};
